import React, { useState, useRef, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import i18n from 'i18next';

import { runSearchAndApply } from '../../services/search-mails-flow';
import { clearApplicationFilteredMessages } from '../../actions/application';
import { clearMessagesFilteredMessages } from '../../actions/messages';
import Chip from '../common/Chip';
import './search-email.scss';

const SearchEmail = ({ lexon, folders, filtersApplied, dispatch }) => {
  const [isFiltersOpen, setFiltersOpen] = useState(false);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const dateFromRef = useRef(null);
  const dateToRef = useRef(null);
  const searchButtonRef = useRef(null);
  const wordsTextBoxRef = useRef(null);
  const [settingsStyle, setSettingsStyle] = useState({});
  const prevIsFiltersOpenRef = useRef(isFiltersOpen);
  const [repopulateOnNextOpen, setRepopulateOnNextOpen] = useState(false);

  // Allow clicks inside Syncfusion popups (dropdowns/datepickers) without closing filters
  const syncfusionOverlaySelectors = [
    '.e-datepicker.e-popup-wrapper',
    '.e-datetimepicker.e-popup',
    '.e-daterangepicker.e-popup',
    '.e-timepicker.e-popup',
    '.e-popup.e-ddl',
    '.e-popup.e-multiselect',
    '.e-popup.e-combobox',
    '.e-dropdownbase.e-popup'
  ];

  const isInsideAnyOverlay = target => {
    if (!target) {
      return false;
    }
    // Check known popup wrappers
    for (let i = 0; i < syncfusionOverlaySelectors.length; i += 1) {
      if (target.closest(syncfusionOverlaySelectors[i])) {
        return true;
      }
    }
    // Many Syncfusion popups use an id suffix `_popup`
    const el = target.closest('[id$="_popup"]');
    return Boolean(el);
  };

  const emptySerachFolder = {
    text: i18n.t("email-search.search-in-all"),
    value: ''
  };

  // Stores filters applied in the last executed search to render as chips
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [quickSearchText, setQuickSearchText] = useState('');
  const [searchFolder, setSearchFolder] = useState(emptySerachFolder.value);
  const [searchFolders, setSearchFolders] = useState([emptySerachFolder]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [words, setWords] = useState('');
  const [attachmentName, setAttachmentName] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showDateErrors, setShowDateErrors] = useState(false);

  // const [lexonSectionOpen, setLexonSectionOpen] = useState(false);
  const [lexonCode, setLexonCode] = useState('');
  const [lexonEmail, setLexonEmail] = useState('');
  const [lexonName, setLexonName] = useState('');

  const { userId, account, lexonToken } = lexon;

  // Update the settings panel position and width when open
  const updateSettingsPosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - (rect.bottom + 8);
      const spaceAbove = rect.top - 8;

      // Prefer opening below; if not enough space and there's more space above, open upwards
      let top = rect.bottom + window.scrollY + 8;
      let maxHeight = Math.max(spaceBelow - 160, 200);

      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        maxHeight = Math.max(spaceAbove - 160, 200);
        top = rect.top + window.scrollY - maxHeight - 8;
      }

      setSettingsStyle({
        position: 'absolute',
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 1000,
        maxHeight,
        overflowY: 'auto'
      });
    }
  }, [containerRef]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isFiltersOpen) {
      updateSettingsPosition();
      window.addEventListener('resize', updateSettingsPosition);
      window.addEventListener('scroll', updateSettingsPosition);
      return () => {
        window.removeEventListener('resize', updateSettingsPosition);
        window.removeEventListener('scroll', updateSettingsPosition);
      };
    }
  }, [isFiltersOpen, updateSettingsPosition]);

  useEffect(() => {
    updateSettingsPosition();
    window.addEventListener('resize', updateSettingsPosition);
    window.addEventListener('scroll', updateSettingsPosition);
    return () => {
      window.removeEventListener('resize', updateSettingsPosition);
      window.removeEventListener('scroll', updateSettingsPosition);
    };
  }, []);

  useEffect(() => {
    if (folders && folders.explodedItems && typeof folders.explodedItems === 'object') {
      const foldersList = Object.values(folders.explodedItems)
        .filter(Boolean)
        .map(folder => ({
          text: folder.name,
          value: folder.fullName
        }));
      foldersList.unshift(emptySerachFolder);
      setSearchFolders(foldersList);
    }
  }, [folders]);

  // Clear filtered messages when component mounts (page reload)
  useEffect(() => {
    dispatch(clearApplicationFilteredMessages());
    dispatch(clearMessagesFilteredMessages());
  }, [dispatch]);

  // Repopulate form from applied filters on the first open after pressing X while open
  useEffect(() => {
    const repopulateFromFilters = fa => {
      setSearchFolder(fa.folder || emptySerachFolder.value);
      setFrom(fa["addressFrom.email"] || fa["addressFrom.name"] || '');
      setTo(fa["addressTo.email"] || fa["addressTo.name"] || '');
      setCc(fa["addressCc.email"] || fa["addressCc.name"] || '');
      setBcc(fa["addressCco.email"] || fa["addressCco.name"] || '');
      setSubject(fa.subject || '');
      setWords(fa.texto || '');
      setAttachmentName(fa["attachments.filename"] || '');
      const dateParam = fa.date || '';
      if (dateParam) {
        const [fromStr, toStr] = String(dateParam).split(',');
        const parseDate = s => {
          if (!s) {
            return null;
          }
          const [yyyy, mm, dd] = s.split('-').map(Number);
          if (!yyyy || !mm || !dd) {
            return null;
          }
          return new Date(yyyy, mm - 1, dd);
        };
        setDateFrom(parseDate(fromStr));
        setDateTo(parseDate(toStr) || null);
      } else {
        setDateFrom(null);
        setDateTo(null);
      }
      setLexonCode(fa["actuaciones.code"] || '');
      setLexonEmail((fa["actuaciones.email"] || ''));
      setLexonName(fa["actuaciones.description"] || '');
    };

    const justOpened = isFiltersOpen && !prevIsFiltersOpenRef.current;
    if (!justOpened) {
      return;
    }

    const fa = filtersApplied || {};
    const hasFiltersApplied = Object.keys(fa).length > 0;
    const hasDraftValues = Boolean(
      (searchFolder && searchFolder !== emptySerachFolder.value) ||
      from || to || cc || bcc || subject || words || attachmentName ||
      dateFrom || dateTo || lexonCode || lexonEmail || lexonName
    );

    if (repopulateOnNextOpen) {
      try {
        // Defer to next tick so inputs are fully mounted before setting values
        setTimeout(() => {
          repopulateFromFilters(fa);
        }, 0);
      } finally {
        setRepopulateOnNextOpen(false);
      }
      return;
    }

    if (hasFiltersApplied && !hasDraftValues) {
      // Defer to next tick to ensure popup inputs are mounted
      setTimeout(() => {
        repopulateFromFilters(fa);
      }, 0);
    }
  }, [isFiltersOpen, repopulateOnNextOpen, filtersApplied]);

  // Keep previous open state in sync even on close
  useEffect(() => {
    prevIsFiltersOpenRef.current = isFiltersOpen;
  }, [isFiltersOpen]);

  // When filters open, focus the words input if empty
  useEffect(() => {
    const justOpened = isFiltersOpen && !prevIsFiltersOpenRef.current;
    if (justOpened && (!words || String(words).trim() === '')) {
      // Defer to ensure DOM and Syncfusion wrapper are mounted
      setTimeout(() => {
        if (wordsTextBoxRef.current && typeof wordsTextBoxRef.current.focusIn === 'function') {
          wordsTextBoxRef.current.focusIn();
          return;
        }
        // Fallback to native focus
        const input = document.getElementById('words') || document.querySelector('#words, #words_input');
        if (input && typeof input.focus === 'function') {
          input.focus();
        }
      }, 50);
    }
  }, [isFiltersOpen, words]);

  // Close filters when clicking outside, but keep them open when interacting with popups
  useEffect(() => {
    const handleDocumentMouseDown = event => {
      const containerEl = containerRef.current;
      const popoverEl = popoverRef.current;
      const target = event.target;

      if ((containerEl && containerEl.contains(target)) || (popoverEl && popoverEl.contains(target))) {
        return;
      }
      if (isInsideAnyOverlay(target)) {
        return;
      }
      setFiltersOpen(false);
    };
    document.addEventListener('mousedown', handleDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, []);

  const formatDateYYYYMMDD = d => {
    if (!d || !(d instanceof Date)) {
      return '';
    }
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Removed unused helpers (startOfDay, endOfDay, addDays) after time-aware validation

  const validateDateRange = (fromDate, toDate) => {
    // If only one of the dates is selected, require completing the range
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      return 'email-search.required_date_range';
    }
    // If both are selected, allow same-day (from <= to). Error only when from > to
    if (fromDate && toDate) {
      if (fromDate.getTime() > toDate.getTime()) {
        return 'email-search.error-date-invalid-order';
      }
    }
    return '';
  };

  const dateValidationError = validateDateRange(dateFrom, dateTo);
  const isSearchDisabled = false;

  // Dynamic min/max limits for the date pickers
  const today = new Date();
  const absoluteMinDate = new Date(1900, 0, 1);
  const dateToMin = dateFrom ? new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate()) : null; // allow same day
  const dateFromMax = dateTo ? new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate()) : today; // allow same day

  // No auto-clearing of dateTo when dateFrom changes; validation and min/max handle constraints

  const collectCurrentValues = () => ({
    searchFolder,
    from,
    to,
    cc,
    bcc,
    subject,
    words,
    attachmentName,
    dateFrom,
    dateTo,
    lexonCode,
    lexonEmail,
    lexonName
  });

  const hasAnyFormValue = () => Boolean(
    (searchFolder && searchFolder !== emptySerachFolder.value) ||
    from || to || cc || bcc || subject || words || attachmentName ||
    dateFrom || dateTo || lexonCode || lexonEmail || lexonName
  );

  const buildAppliedFiltersFromValues = values => {
    const filters = [];
    if (values.searchFolder) {
      const selected = (searchFolders || []).find(f => f.value === values.searchFolder);
      const folderLabel = selected ? selected.text : values.searchFolder;
      filters.push({ key: 'folder', label: i18n.t('email-search.search-in'), value: folderLabel });
    }
    if (values.from) {
      filters.push({ key: 'from', label: i18n.t('email-search.from'), value: values.from });
    }
    if (values.to) {
      filters.push({ key: 'to', label: i18n.t('email-search.to'), value: values.to });
    }
    if (values.cc) {
      filters.push({ key: 'cc', label: i18n.t('email-search.cc'), value: values.cc });
    }
    if (values.bcc) {
      filters.push({ key: 'bcc', label: i18n.t('email-search.bcc'), value: values.bcc });
    }
    if (values.subject) {
      filters.push({ key: 'subject', label: i18n.t('email-search.subject'), value: values.subject });
    }
    if (values.words) {
      filters.push({ key: 'words', label: i18n.t('email-search.words'), value: values.words });
    }
    if (values.attachmentName) {
      filters.push({ key: 'attachmentName', label: i18n.t('email-search.attachment-name'), value: values.attachmentName });
    }
    if (values.dateFrom || values.dateTo) {
      const human = [
        values.dateFrom ? new Intl.DateTimeFormat('es-ES').format(values.dateFrom) : null,
        values.dateTo ? new Intl.DateTimeFormat('es-ES').format(values.dateTo) : null
      ].filter(Boolean).join(' - ');
      filters.push({ key: 'dateRange', label: i18n.t('email-search.date-range'), value: human });
    }
    if (values.lexonCode) {
      filters.push({ key: 'lexonCode', label: i18n.t('email-search.lexon-code'), value: values.lexonCode });
    }
    if (values.lexonEmail) {
      filters.push({ key: 'lexonEmail', label: i18n.t('email-search.lexon-email'), value: values.lexonEmail });
    }
    if (values.lexonName) {
      filters.push({ key: 'lexonName', label: i18n.t('email-search.lexon-name'), value: values.lexonName });
    }
    return filters;
  };

  const buildSearchBodyFromValues = values => {
    let dateParam = null;
    if (values.dateFrom && values.dateTo) {
      dateParam = `${formatDateYYYYMMDD(values.dateFrom)},${formatDateYYYYMMDD(values.dateTo)}`;
    } else if (values.dateFrom) {
      dateParam = `${formatDateYYYYMMDD(values.dateFrom)}`;
    }

   return {
      ...(values.searchFolder && { folder: values.searchFolder }),
      ...(values.from && (String(values.from).includes('@')
        ? { "addressFrom.email": String(values.from).toLowerCase() }
        : { "addressFrom.name": values.from })),
      ...(values.to && (String(values.to).includes('@')
        ? { "addressTo.email": String(values.to).toLowerCase() }
        : { "addressTo.name": values.to })),
      ...(values.cc && (String(values.cc).includes('@')
        ? { "addressCc.email": String(values.cc).toLowerCase() }
        : { "addressCc.name": values.cc })),
      ...(values.bcc && (String(values.bcc).includes('@')
        ? { "addressCco.email": String(values.bcc).toLowerCase() }
        : { "addressCco.name": values.bcc })),
      ...(values.subject && { subject: values.subject }),
      ...(values.words && { texto: values.words }),
      ...(values.attachmentName && { "attachments.filename": values.attachmentName }),
      ...(dateParam && { date: dateParam }),
      ...(values.lexonCode && { "actuaciones.code": values.lexonCode }),
      ...(values.lexonEmail && { 'actuaciones.email': values.lexonEmail.toLowerCase() }),
      ...(values.lexonName && { 'actuaciones.description': values.lexonName })
    };
  };

  // Keep chips in sync if Redux filters change externally
  useEffect(() => {
    const fa = filtersApplied || {};

    const dateParam = fa.date || '';
    let parsedFrom = null;
    let parsedTo = null;
    if (dateParam) {
      const [fromStr, toStr] = String(dateParam).split(',');
      const parseDate = s => {
        if (!s) {
          return null;
        }
        const [yyyy, mm, dd] = s.split('-').map(Number);
        if (!yyyy || !mm || !dd) {
          return null;
        }
        return new Date(yyyy, mm - 1, dd);
      };
      parsedFrom = parseDate(fromStr);
      parsedTo = parseDate(toStr) || null;
    }

    const valuesFromFa = {
       searchFolder: fa.folder || emptySerachFolder.value,
      from: fa["addressFrom.email"] || fa["addressFrom.name"] || '',
      to: fa["addressTo.email"] || fa["addressTo.name"] || '',
      cc: fa["addressCc.email"] || fa["addressCc.name"] || '',
      bcc: fa["addressCco.email"] || fa["addressCco.name"] || '',
      subject: fa.subject || '',
      words: fa.texto || '',
      attachmentName: fa["attachments.filename"] || '',
      dateFrom: parsedFrom,
      dateTo: parsedTo,
      lexonCode: fa["actuaciones.code"] || '',
      lexonEmail: fa['actuaciones.email'] || '',
      lexonName: fa['actuaciones.description'] || ''
    };

    setAppliedFilters(buildAppliedFiltersFromValues(valuesFromFa));
  }, [filtersApplied, searchFolders]);

  const performSearch = async values => {
    const error = validateDateRange(values.dateFrom, values.dateTo);
    if (error) {
      setShowDateErrors(true);
      setFiltersOpen(true);
      // Ensure the search button does not remain in active state after error
      if (searchButtonRef.current && typeof searchButtonRef.current.blur === 'function') {
        // Defer blur to next tick to avoid interfering with click processing
        setTimeout(() => searchButtonRef.current.blur(), 0);
      } else if (document.activeElement && typeof document.activeElement.blur === 'function') {
        setTimeout(() => document.activeElement.blur(), 0);
      }
      return;
    }

    const body = buildSearchBodyFromValues(values);
    setFiltersOpen(false);
    setAppliedFilters(buildAppliedFiltersFromValues(values));

    await runSearchAndApply(dispatch, { userId, account, lexonToken, body });
  };

  const handleSearch = async () => {
    try {
      const values = collectCurrentValues();
      await performSearch(values);
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  // Hide date errors until next search attempt after user changes dates
  useEffect(() => {
    if (showDateErrors) {
      setShowDateErrors(false);
    }
  }, [dateFrom, dateTo]);

  const handleQuickSearchSubmit = async () => {
    const text = (quickSearchText || '').trim();
    if (!text) {
      return;
    }
    try {
      setWords(text);
      const values = { ...collectCurrentValues(), words: text };
      await performSearch(values);
      setQuickSearchText('');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Quick search error:', error);
    }
  };

  const handleClearDraft = () => {
    // Always clear local draft form values
    setFrom('');
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setWords('');
    setAttachmentName('');
    setDateFrom(null);
    setDateTo(null);
    if (dateFromRef.current && typeof dateFromRef.current.clear === 'function') {
      dateFromRef.current.clear();
    }
    if (dateToRef.current && typeof dateToRef.current.clear === 'function') {
      dateToRef.current.clear();
    }
    setLexonCode('');
    setLexonEmail('');
    setLexonName('');
    setSearchFolder(emptySerachFolder.value);

    // If there are applied filters, also clear the search results in Redux
    if (appliedFilters && appliedFilters.length > 0) {
      setAppliedFilters([]);
      dispatch(clearApplicationFilteredMessages());
      dispatch(clearMessagesFilteredMessages());
      setRepopulateOnNextOpen(false);
      return;
    }

    if (isFiltersOpen) {
      // No applied filters: just arm repopulation for next open
      setRepopulateOnNextOpen(true);
    } else {
      setAppliedFilters([]);
      dispatch(clearApplicationFilteredMessages());
      dispatch(clearMessagesFilteredMessages());
      setRepopulateOnNextOpen(false);
    }
  };

  const handleRemoveChip = async key => {
    // Snapshot current values and mutate the copy to avoid stale state in search
    const values = collectCurrentValues();
    switch (key) {
      case 'folder':
        setSearchFolder(emptySerachFolder.value);
        values.searchFolder = emptySerachFolder.value;
        break;
      case 'from':
        setFrom('');
        values.from = '';
        break;
      case 'to':
        setTo('');
        values.to = '';
        break;
      case 'cc':
        setCc('');
        values.cc = '';
        break;
      case 'bcc':
        setBcc('');
        values.bcc = '';
        break;
      case 'subject':
        setSubject('');
        values.subject = '';
        break;
      case 'words':
        setWords('');
        values.words = '';
        break;
      case 'attachmentName':
        setAttachmentName('');
        values.attachmentName = '';
        break;
      case 'dateRange':
        setDateFrom(null);
        setDateTo(null);
        values.dateFrom = null;
        values.dateTo = null;
        if (dateFromRef.current && typeof dateFromRef.current.clear === 'function') {
          dateFromRef.current.clear();
        }
        if (dateToRef.current && typeof dateToRef.current.clear === 'function') {
          dateToRef.current.clear();
        }
        break;
      case 'lexonCode':
        setLexonCode('');
        values.lexonCode = '';
        break;
      case 'lexonEmail':
        setLexonEmail('');
        values.lexonEmail = '';
        break;
      case 'lexonName':
        setLexonName('');
        values.lexonName = '';
        break;
      default:
        break;
    }

    // Trigger refreshed search with the updated values
    await performSearch(values);
  };

  return (
    <>
      <div className={'search-container'} ref={containerRef}>
        <div
          className={'search-section'}
          onClick={e => {
            // If there are no chips, this section behaves like a text input area, so do not toggle the filters
            if (!appliedFilters || appliedFilters.length === 0) {
              e.stopPropagation();
              return;
            }
            setFiltersOpen(open => !open);
          }}
        >
          <div className={'chips-container'}>
            {(!appliedFilters || appliedFilters.length === 0) ? (
              <div
                className={'quick-search'}
                onClick={e => e.stopPropagation()}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    handleQuickSearchSubmit();
                  }
                }}
              >
                <input
                  type="text"
                  value={quickSearchText}
                  onChange={e => setQuickSearchText(e.target.value)}
                  placeholder={i18n.t('email-search.search-placeholder')}
                  className={'quick-search-input'}
                  aria-label="Quick search"
                />
              </div>
            ) : (
              (appliedFilters || []).map(filter => (
                <Chip
                  key={filter.key}
                  label={`${filter.label}: ${filter.value}`}
                  onClose={() => handleRemoveChip(filter.key)}
                  closeAriaLabel={`remove ${filter.label}`}
                />
              ))
            )}
          </div>
        </div>
        <div className={'action-section'}>
          {(
            (appliedFilters && appliedFilters.length > 0) ||
            (hasAnyFormValue() && isFiltersOpen)
          ) && (
            <button
              className={'action-button'}
              onClick={handleClearDraft}
              aria-label="Clear form"
            >
              <img
                src="/assets/images/close-icon-grey.svg"
                alt="Clear"
                width={22}
                height={22}
              />
            </button>
          )}
          <button
            className={'action-button'}
            id="settings-popover-btn"
            onClick={() => setFiltersOpen(open => !open)}
            aria-label="Open search settings"
          >
            <img
              src="/assets/images/icon-settings-grey.svg"
              alt="Settings"
              width={22}
              height={22}
            />
          </button>
          {(
            (appliedFilters && appliedFilters.length === 0)
          ) && (
            <button
              type="button"
              className={'action-button'}
              onClick={handleQuickSearchSubmit}
              aria-label="Execute quick search"
            >
              <img
                src="/assets/images/icon-search-grey.svg"
                alt="Search"
                width={20}
                height={20}
              />
            </button>
          )}
        </div>
      </div>
      {isFiltersOpen && (
        <div
          className={'search-popover-body'}
          style={settingsStyle}
          ref={popoverRef}
        >
          <div className={`form-group search-popover-body-form-group`}>
            <div className={'input-group-wrapper'}>
              <div className={`e-float-input search-column`}>
                <label htmlFor="search-folder-dropdown" className={'search-column-label'}>
                  {i18n.t("email-search.search-in")}
                </label>
                <div className={'search-column-input'}>
                  <DropDownListComponent
                    id="search-folder-dropdown"
                    dataSource={searchFolders}
                    placeholder={i18n.t("email-search.search-in-all")}
                    value={searchFolder}
                    fields={{ text: 'text', value: 'value' }}
                    change={e => {
                      setSearchFolder(e.value);
                    }}
                    className='field-input'
                  />
                  <span className="input-divider" />
                </div>
              </div>
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="from" className={'search-column-label'}>
                  {i18n.t("email-search.from")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='from'
                    input={e => {
                      setFrom(e.value);
                    }}
                    placeholder={i18n.t("email-search.email-placeholder")}
                    value={from}
                    className='field-input'
                  />
                </div>
              </div>
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="to" className={'search-column-label'}>
                  {i18n.t("email-search.to")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='to'
                    input={e => {
                      setTo(e.value);
                    }}
                    placeholder={i18n.t("email-search.email-placeholder")}
                    value={to}
                    className='field-input'
                  />
                </div>
              </div>
              {/* CC */}
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="cc" className={'search-column-label'}>
                  {i18n.t("email-search.cc")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='cc'
                    input={e => {
                      setCc(e.value);
                    }}
                    placeholder={i18n.t("email-search.email-placeholder")}
                    value={cc}
                    className='field-input'
                  />
                </div>
              </div>
              {/* BCC */}
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="bcc" className={'search-column-label'}>
                  {i18n.t("email-search.bcc")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='bcc'
                    input={e => {
                      setBcc(e.value);
                    }}
                    placeholder={i18n.t("email-search.email-placeholder")}
                    value={bcc}
                    className='field-input'
                  />
                </div>
              </div>
              {/* Subject */}
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="subject" className={'search-column-label'}>
                  {i18n.t("email-search.subject")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='subject'
                    input={e => {
                      setSubject(e.value);
                    }}
                    placeholder={i18n.t("email-search.email-subject-placeholder")}
                    value={subject}
                    className='field-input'
                  />
                </div>
              </div>
              {/* Words */}
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="words" className={'search-column-label'}>
                  {i18n.t("email-search.words")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='words'
                    ref={wordsTextBoxRef}
                    input={e => {
                      setWords(e.value);
                    }}
                    placeholder={i18n.t("email-search.words")}
                    value={words}
                    className='field-input'
                  />
                </div>
              </div>
              {/* Attachment Name */}
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="attachmentName" className={'search-column-label'}>
                  {i18n.t("email-search.attachment-name")}
                </label>
                <div className={'search-column-input'}>
                  <TextBoxComponent
                    id='attachmentName'
                    input={e => {
                      setAttachmentName(e.value);
                    }}
                    placeholder={i18n.t("email-search.attachment-name-placeholder")}
                    value={attachmentName}
                    className='field-input'
                  />
                </div>
              </div>
              <div className={`e-float-input ${'search-column'}`}>
                <label htmlFor="dateFrom" className={'search-column-label'}>
                  {i18n.t("email-search.date-range")}
                </label>
                <div className={'search-column-input'}>
                  <DatePickerComponent
                    id='dateFrom'
                    format="dd/MM/yyyy"
                    max={dateFromMax}
                    allowEdit={false}
                    ref={dateFromRef}
                    change={e => {
                      setDateFrom(e.value);
                    }}
                    placeholder={i18n.t("email-search.date-placeholder")}
                    value={dateFrom}
                    cssClass='search-date-picker'
                    className='field-input'
                  />
                  <span className={'dates-separator-label'}>{i18n.t("email-search.date-separator-label")}</span>
                  <DatePickerComponent
                    id='dateTo'
                    format="dd/MM/yyyy"
                    max={new Date()}
                    allowEdit={false}
                    min={dateToMin || absoluteMinDate}
                    ref={dateToRef}
                    change={e => {
                      setDateTo(e.value);
                    }}
                    placeholder={i18n.t("email-search.date-placeholder")}
                    value={dateTo}
                    cssClass='search-date-picker'
                    className='field-input'
                  />
                </div>
              </div>
              {(showDateErrors && dateValidationError) ? (
                <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 6 }}>
                  {i18n.t(dateValidationError)}
                </div>
              ) : null}
            </div>
            <div className={'search-buttons-actions'}>
              <button
                type="button"
                className="mr-left font-weight-bold btn-outline-primary btn btn-secondary"
                onClick={() => {
                  setFrom('');
                  setTo('');
                  setCc('');
                  setBcc('');
                  setSubject('');
                  setWords('');
                  setAttachmentName('');
                  setDateFrom(null);
                  setDateTo(null);
                  if (dateFromRef.current && typeof dateFromRef.current.clear === 'function') {
                    dateFromRef.current.clear();
                  }
                  if (dateToRef.current && typeof dateToRef.current.clear === 'function') {
                    dateToRef.current.clear();
                  }
                  setLexonCode('');
                  setLexonEmail('');
                  setLexonName('');
                  setSearchFolder(emptySerachFolder.value);
                  // Ensure the next open repopulates from Redux filters
                  setRepopulateOnNextOpen(true);
                }}
              >
                {i18n.t("email-search.clear-filters")}
              </button>
              <button
                type="button"
                className="mr-left font-weight-bold btn-primary btn"
                ref={searchButtonRef}
                onClick={handleSearch}
                disabled={isSearchDisabled}
                title={dateValidationError ? i18n.t(dateValidationError) : ''}
              >
                {i18n.t("email-search.search-button")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => ({
  lexon: state.lexon,
  folders: state.folders,
  filtersApplied: state.application && state.application.filtersApplied
});

export default connect(
  mapStateToProps
)(SearchEmail);
